import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
  HiOutlineArrowPath,
} from "react-icons/hi2";
import Button from "components/button/Button";
import { Assessment } from "types/assessmentTypes";
import { FaRegEdit } from "react-icons/fa";
import { useGetSettingsQuery } from "reduxFiles/api/authApi";
import moment from "moment";
import { useScope } from "hooks/useScope";

interface AssessmentHeaderProps {
  assessment: Assessment;
  isAssessmentComplete: boolean;
  allRespondersCompleted: boolean;
  isQuantitativeFilled: boolean;
  isSubmitting: boolean;
  canComplete: boolean;
  onBack: () => void;
  onComplete: () => void;
  onRefresh: () => void;
  onUpdated: () => void;
  isRefreshing: boolean;
  renderStatusBadge: (status: string) => React.ReactNode;
}

export default function AssessmentHeader({
  assessment,
  isAssessmentComplete,
  allRespondersCompleted,
  isQuantitativeFilled,
  isSubmitting,
  canComplete,
  onBack,
  onComplete,
  onRefresh,
  onUpdated,
  isRefreshing,
  renderStatusBadge,
}: AssessmentHeaderProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scope = useScope();

  const { data: settings } = useGetSettingsQuery();

  const canEditAssessment = (): boolean => {
    if (!assessment.completedAt || !settings?.settingsJson?.limitUpdatedDay)
      return false;

    const completedDate = moment(assessment.completedAt);
    const now = moment();

    const expirationDate = completedDate
      .clone()
      .add(settings?.settingsJson?.limitUpdatedDay, "days");

    return now.isSameOrBefore(expirationDate, "day");
  };

  const canEdit = canEditAssessment();
  const showRestart =
    (scope === "admin" && isAssessmentComplete) ||
    (scope === "user" &&
      isAssessmentComplete &&
      !assessment.updated &&
      canEdit);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-800 transition-colors hover:text-brand-500 dark:text-gray-400"
        >
          <HiOutlineArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t("assessment.detail.back")}
        </button>

        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-800 transition-colors hover:bg-gray-100 hover:text-brand-500 dark:text-gray-400 dark:hover:bg-navy-700"
        >
          <HiOutlineArrowPath
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          {t("assessment.detail.refresh")}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-navy-700 dark:text-white md:text-2xl">
            {t("assessment.detail.title", {
              companyName: assessment.company.name,
            })}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="hidden sm:inline">•</span>
            <span dir="ltr">
              {t("assessment.detail.createdAt")} :{" "}
              {new Date(assessment.createdAt).toLocaleDateString(
                lang === "fa" ? "fa-IR" : "en-US"
              )}
            </span>
            <span className="hidden sm:inline">•</span>
            {assessment.completedAt && (
              <span dir="ltr">
                {t("organizationAssessmentReport.completedAt")} :{" "}
                {new Date(assessment.completedAt).toLocaleDateString(
                  lang === "fa" ? "fa-IR" : "en-US"
                )}
              </span>
            )}
          </div>
        </div>

        {!isAssessmentComplete && (
          <div
            className={`
      mt-4 inline-block rounded-lg border p-2.5 px-4 transition-all duration-500
      ${
        allRespondersCompleted && isQuantitativeFilled
          ? "dark:bg-green-950/20 border-green-200 bg-green-50 dark:border-green-800/40"
          : "dark:bg-amber-950/20 border-amber-200 bg-amber-50 dark:border-amber-800/40"
      }
    `}
          >
            <div className="flex items-center gap-2">
              <div
                className={`
          flex h-5 w-5 items-center justify-center rounded-full transition-all duration-500
          ${
            allRespondersCompleted && isQuantitativeFilled
              ? "bg-green-200 text-green-600 dark:bg-green-900/40 dark:text-green-400"
              : "bg-amber-200 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400"
          }
        `}
              >
                {allRespondersCompleted && isQuantitativeFilled ? (
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>

              <span
                className={`text-xs font-medium ${
                  allRespondersCompleted && isQuantitativeFilled
                    ? "text-green-700 dark:text-green-300"
                    : "text-amber-800 dark:text-amber-300"
                }`}
              >
                {allRespondersCompleted && isQuantitativeFilled
                  ? t("assessment.detail.complete.ready")
                  : t("assessment.detail.complete.guideTitle")}
              </span>
            </div>

            <div className="mt-1.5 flex flex-col gap-x-3 gap-y-1 text-xs">
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    allRespondersCompleted ? "bg-green-500" : "bg-yellow-500"
                  }`}
                />
                <span className="whitespace-nowrap text-gray-800 dark:text-gray-400">
                  {t("assessment.detail.complete.steps.responders")}
                </span>
                {allRespondersCompleted && (
                  <span className="text-[10px] text-green-600 dark:text-green-400">
                    ✓
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    isQuantitativeFilled ? "bg-green-500" : "bg-yellow-500"
                  }`}
                />
                <span className="whitespace-nowrap text-gray-800 dark:text-gray-400">
                  {t("assessment.detail.complete.steps.quantitative")}
                </span>
                {isQuantitativeFilled && (
                  <span className="text-[10px] text-green-600 dark:text-green-400">
                    ✓
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <div className="h-1 w-12 overflow-hidden rounded-full bg-gray-200 dark:bg-navy-600">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      allRespondersCompleted && isQuantitativeFilled
                        ? "w-full bg-green-500"
                        : allRespondersCompleted || isQuantitativeFilled
                        ? "w-1/2 bg-yellow-500"
                        : "w-0 bg-amber-400"
                    }`}
                  />
                </div>
                <span className="text-[10px] font-medium text-gray-700 dark:text-gray-400">
                  {
                    [allRespondersCompleted, isQuantitativeFilled].filter(
                      Boolean
                    ).length
                  }
                  /2
                </span>
              </div>
            </div>
          </div>
        )}

        {renderStatusBadge(assessment.status)}

        {!isAssessmentComplete && (
          <Button
            variant="success"
            size="md"
            onClick={onComplete}
            disabled={!canComplete || isSubmitting}
            startIcon={<HiOutlineCheckCircle className="h-5 w-5" />}
          >
            {t("assessment.detail.complete.button")}
          </Button>
        )}

        {showRestart && (
          <Button
            variant="error"
            size="md"
            onClick={onUpdated}
            startIcon={<FaRegEdit className="h-4 w-4" />}
          >
            {t(
              `assessment.detail.updated.${
                scope === "admin" ? "adminButton" : "button"
              }`
            )}
          </Button>
        )}
      </div>
    </motion.div>
  );
}
